import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import PlanDetailsAPI from "../../../API/PlanDetailsAPI";
import PlanFilesAPI, { ACCEPT, MAX_FILE_MB, checkFile, formatSize } from "../../../API/PlanFilesAPI";
import VolumesAPI from "../../../API/VolumesAPI";
import Header from "../../Header";
import LoadingSpinner from "../../Shared/LoadingSpinner";
import { calcVolumes, defaultThresholds, thresholdDifferences } from "../TotalVolume/calcVolumes";
import "../TotalVolume/TotalVolume.css";
import { HYBRID_SHORTFALL, bundleCheck, partnerBoxes, resistancePrices } from "./bundleCheck";
import { createHybridPlan, planItems, plantingBody } from "./hybridPlan";
import HybridPlanAPI from "./HybridPlanAPI";
import "./HybridVolume.css";

// Volume order + resistance-bundle check in one screen (the "Hybrid sheet" of the Bundling Offer workbook).
// The user orders every product as a normal line. For every bundle the server knows (pricing2Dependants; Silvalis today),
// the partner volumes are checked once its main product is ordered.
// The check only decides the PRICE of the main product (resistance price or list price). It never blocks the order.

const MAX_LINES = 20;
const newLine = () => ({ key: crypto.randomUUID(), rowId: null, boxes: null });

const money = (v) => (v === null || v === undefined ? "" : `R ${Math.round(v).toLocaleString("en-ZA").replace(/,/g, " ")}`);
const price = (v) => (v === null || v === undefined ? "" : `R ${v.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const dose = (v) => (v === null || v === undefined ? "" : v.toLocaleString("en-ZA", { maximumFractionDigits: 3 }));
const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v));
const qty = (v) => (v === null || v === undefined ? "" : Math.round(v).toLocaleString("en-ZA").replace(/,/g, " "));

// The sheet's check cell: coloured text, "Geen limiete" when the product has no min or max
const checkCell = (checks, min, max, hasVolume) => {
  if (checks.length > 0) {
    return checks.map((c) => (
      <span key={c} className="hv-chk hv-chk-bad">
        {CHECK_LABEL[c] || c}
      </span>
    ));
  }
  if (!hasVolume) return null;
  return (min !== null && min !== undefined) || (max !== null && max !== undefined) ? (
    <span className="hv-chk hv-chk-ok">{CHECK_LABEL.OK}</span>
  ) : (
    <span className="hv-chk">Geen limiete</span>
  );
};
// Vol/ha cell: red fill when over max / under min, green when within its limits (as the sheet's conditional format)
const volClass = (r) => {
  if (!r || !r.volume) return "";
  if (r.checks.some((c) => c === "Over max" || c === "Under min")) return "hv-bad-cell";
  if (r.checks.includes("Duplicate")) return "";
  return r.minDosage !== null || r.maxDosage !== null ? "hv-good-cell" : "";
};

// Hover text for the headers: how each value is worked out
const TIPS = {
  produk:
    "Kies die produk en verpakking. 'n Produk in twee verpakkings gebruik twee reëls; hulle word saam getel in die subtotaalreël.\n\nBron: tabel pricing2 (product_name, product_pack_name), vir die gewas.",
  kode: "Die AGI-kode van die verpakking.\n\nBron: pricing2.AGI.",
  pak: "Liter of kg in een boks.\n\nBron: pricing2.full_pack_volume (= unit_volume × full_pack_units).",
  bokse: "Die aantal bokse wat jy bestel.\n\nBron: jy tik dit self in; dit kom nie uit die databasis nie.",
  volume: "Bokse × pakgrootte.\n\nBron: bokse (jy) × pricing2.full_pack_volume.",
  volHa:
    "Volume ÷ hektaar. Is 'n produk op twee reëls, dan tel die twee verpakkings saam (sien die subtotaalreël).\n\nBron: volume en die Hektaar-veld onder INSETTE.",
  min: "Kleinste dosis per ha waarvoor die Vlak 2-afslag geld.\n\nBron: pricing2.tier2_min_dosage.",
  maks: "Grootste dosis per ha. Net die volume tot (maks × hektaar) kwalifiseer vir die vlak-afslag.\n\nBron: pricing2.max_dosage.",
  toets:
    "Reg = binne die minimum en maksimum. Bo maks / Onder min = daarbuite. Geen limiete = geen minimum of maksimum. Duplikaat = dieselfde verpakking twee keer.\n\nBron: Vol/ha teenoor pricing2.tier2_min_dosage en pricing2.max_dosage.",
  lysPrys:
    "Lysprys per liter of kg vir hierdie verpakking.\n\nBron: pricing2.list_price_pv. Het die agent se SAP-rekening 'n eie lysprys, dan kom dit uit agentProductPricing.listPricePv.",
  mypackPrys:
    "Gemiddelde prys na afslag = (lys koste − afslag) ÷ volume. Die afslag volg die huidige vlak en geld net tot die maksimum. * = die laer seisoenprys is toegepas. Is die weerstandsvoorwaardes nagekom, geld die weerstandsprys op die hele volume. Anders volg die prys die produk se eie vlak-instellings (die databasis).\n\nBron: pricing2.tier1_price_pv, tier2_price_pv, tier3_discount_pct en season_price_pv (seisoenprys uit agentProductPricing.discountPv as die agent 'n eie het); weerstandsprys: pricing2.resistance_price_pv. Vlakgrense: blok MYPACK VLAKKE.",
  lysKoste: "Volume × lysprys.\n\nBron: volume × pricing2.list_price_pv.",
  mypackKoste: "Volume × MyPack prys.\n\nBron: bereken uit die kolomme links.",
  lysHa: "Lys koste ÷ hektaar.\n\nBron: Lys koste en die Hektaar-veld onder INSETTE.",
  vennoot:
    "Die produkte wat saam met die hoofproduk bestel moet word vir die weerstandsprys. Waar daar twee is (Sorba of Denim Fit) kies jy een.\n\nBron: tabel pricing2Dependants (role = PARTNER, per group_no); die produknaam uit pricing2.",
  dosis: "Dosis per hektaar per spuit.\n\nBron: pricing2Dependants.dose_per_spray.",
  spuite: "Aantal spuite per seisoen.\n\nBron: pricing2Dependants.sprays_per_season.",
  minNodig:
    "Hektaar wat die hoofproduk dek × dosis × spuite, minus die toelaatbare tekort, na bo afgerond. Hektaar = hoofproduk-volume ÷ (dosis × spuite).\n\nBron: pricing2Dependants (dose_per_spray, sprays_per_season) en die tekort: 1 − pricing2Dependants.min_cover, tensy jy dit onder INSETTE oorskryf.",
  huidig: "Die totale volume van hierdie produk in die tabel hieronder (al sy verpakkings saam).\n\nBron: die VOLUME BESTELLING-tabel (jou bokse).",
  vennootToets: "Reg as huidig bestel ≥ minimum nodig.\n\nBron: die twee kolomme links.",
  afslag:
    "Ja as al die vennote op of bo hul minimum bestel is. Anders geld die weerstandsprys nie en volg die prys die produk se eie vlak-instellings. Dit keer nooit die bestelling nie.\n\nBron: pricing2Dependants (bundel) en pricing2.resistance_price_pv.",
  tier1: "Kleinste lys R/ha vir Vlak 1.\n\nBron: pricing2.tier1_min_rpha (eerste produk van die gewas). Jy kan dit hier verander om te toets.",
  tier2: "Kleinste lys R/ha vir Vlak 2.\n\nBron: pricing2.tier2_min_rpha (eerste produk van die gewas). Jy kan dit hier verander om te toets.",
  tier3: "Kleinste totale lys faktuur vir Vlak 3.\n\nBron: pricing2.tier3_min_invoice (eerste produk van die gewas). Jy kan dit hier verander om te toets.",
  tekort:
    "Hoeveel 'n vennoot onder sy minimum mag bly. Staan op die waarde uit die databasis (1 − pricing2Dependants.min_cover); tik 'n getal om dit te oorskryf.",
  kvFaktuur: "Alle reëls se lys koste saam, ook die weerstandsproduk.\n\nBron: die Lys koste-kolom in die tabel.",
  kvVlak:
    "Vlak 3 as die totale lys faktuur by die Vlak 3-minimum uitkom; anders Vlak 2 of Vlak 1 volgens lys R/ha.\n\nBron: die drie vlakgrense hierbo (pricing2.tier1_min_rpha, tier2_min_rpha, tier3_min_invoice).",
  kvVolgende: "Hoeveel meer lys faktuur nodig is vir die volgende vlak.\n\nBron: die drie vlakgrense hierbo.",
};
const tip = (text, extra = "") => ({ className: `hv-tip ${extra}`.trim(), "data-pr-tooltip": text });

const TIER_LABEL = { 0: "Geen vlak", 1: "Vlak 1", 2: "Vlak 2", 3: "Vlak 3" };
// The sheet's check wording (the calculation keeps the English keys)
const CHECK_LABEL = { "Over max": "Bo maks", "Under min": "Onder min", Duplicate: "Duplikaat – verwyder", OK: "Reg" };

const HybridVolume = (props) => {
  const [rows, setRows] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [bundlesFailed, setBundlesFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [farm, setFarm] = useState("");
  const [hectares, setHectares] = useState(100);
  const [shortfallPct, setShortfallPct] = useState(null); // null = the database min_cover of each bundle group; typing a value overrides it
  const [choices, setChoices] = useState({}); // "<main>:<group>" -> productId (Sorba or Denim Fit)
  const [lines, setLines] = useState(() => Array.from({ length: 5 }, newLine));
  const [thresholds, setThresholds] = useState({ tier1MinRpha: 0, tier2MinRpha: 0, tier3MinInvoice: 0 });
  const [notice, setNotice] = useState("");
  const [infoOpen, setInfoOpen] = useState(true); // the bundle info block: open until the products are added
  // "Heg spuit program aan": create a normal plan (and its order products) from these lines
  const [plan, setPlan] = useState({
    open: false,
    busy: false,
    error: "",
    done: null,
    farmers: [],
    farmerId: null,
    name: "",
    choices: [],
    periods: [],
    crops: [],
  });
  const patchPlan = (patch) => setPlan((p) => ({ ...p, ...patch }));
  // Documents picked here are kept in the browser until the plan exists, then saved against it
  const [docs, setDocs] = useState([]);
  const [docError, setDocError] = useState("");
  const docInput = useRef(null);
  const pickDocs = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = ""; // the same file can be picked again
    const problems = chosen.map(checkFile).filter(Boolean);
    setDocError(problems.join(". "));
    const ok = chosen.filter((f) => !checkFile(f) && !docs.some((d) => d.name === f.name && d.size === f.size));
    if (ok.length) setDocs((d) => [...d, ...ok]);
  };
  const soon = (text) => {
    setNotice(text);
    setTimeout(() => setNotice(""), 4000);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setBundlesFailed(false);
    Promise.all([
      VolumesAPI.getVolumePricing(props.cropId),
      // Without the bundle data the screen still works as a plain volume order
      VolumesAPI.getResistanceBundles(props.cropId).catch(() => null),
    ])
      .then(([data, bundleData]) => {
        if (cancelled) return;
        setRows(data);
        setThresholds(defaultThresholds(data));
        setBundles(Array.isArray(bundleData) ? bundleData : []);
        setBundlesFailed(bundleData === null);
        setChoices({});
        setLines(Array.from({ length: 5 }, newLine));
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [props.cropId]);

  const openPlan = async () => {
    if (!filled.length) {
      soon("Kies eers produkte en bokse.");
      return;
    }
    patchPlan({ open: true, busy: true, error: "", done: null, name: farm || "" });
    try {
      const [farmers, choicesList, periods, crops] = await Promise.all([
        HybridPlanAPI.getFarmers(),
        HybridPlanAPI.getChoices(),
        HybridPlanAPI.getPeriods(),
        HybridPlanAPI.getCrops(),
      ]);
      patchPlan({ farmers, choices: choicesList, periods, crops, busy: false });
    } catch (e) {
      patchPlan({ busy: false, error: e.message });
    }
  };
  const differing = useMemo(() => thresholdDifferences(rows), [rows]);
  const shortfall = shortfallPct === null ? null : (shortfallPct ?? 0) / 100;
  // What the input shows while it is not overridden: the largest shortfall the database allows
  const dbShortfallPct = useMemo(() => {
    const covers = (bundles || []).flatMap((b) => (b.groups || []).map((g) => Number(g.minCover))).filter((v) => v > 0);
    return covers.length ? Math.round((1 - Math.min(...covers)) * 1000) / 10 : HYBRID_SHORTFALL * 100;
  }, [bundles]);

  const filled = useMemo(() => lines.filter((l) => l.rowId).map((l) => ({ ...l })), [lines]);
  const checks = useMemo(() => bundleCheck(rows, filled, bundles, shortfall, choices), [rows, filled, bundles, shortfall, choices]);
  const resistance = useMemo(() => resistancePrices(rows, checks), [rows, checks]);

  const result = useMemo(() => calcVolumes(rows, filled, hectares, thresholds, { resistance }), [rows, filled, hectares, thresholds, resistance]);
  const planPreview = useMemo(
    () => (plan.open ? planItems(rows, filled, hectares, checks, plan.choices) : { items: [], skipped: [] }),
    [plan.open, plan.choices, rows, filled, hectares, checks],
  );
  const resultByKey = useMemo(() => {
    const map = new Map();
    filled.forEach((l, i) => map.set(l.key, result.lines[i]));
    return map;
  }, [filled, result]);

  const groupAfterKey = useMemo(() => {
    const map = new Map();
    (result.groups || []).forEach((g) => map.set(filled[Math.max(...g.indexes)].key, g));
    return map;
  }, [filled, result]);

  const updateLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : [newLine()]));

  const productOptions = (line) =>
    rows.filter((r) => r.id === line.rowId || !lines.some((l) => l.rowId === r.id)).map((r) => ({ label: r.product_pack_name || r.product_name, value: r.id }));

  const setThreshold = (field) => (e) => setThresholds((t) => ({ ...t, [field]: e.value ?? 0 }));

  // Fill in the minimum for every partner that is short: replaces that product's lines
  const addMinimumPartners = () => {
    setLines((ls) => {
      let next = ls.filter((l) => l.rowId);
      checks.forEach((c) =>
        c.partners
          .filter((p) => !p.ok)
          .forEach((p) => {
            const ids = new Set(rows.filter((r) => r.product_id === p.productId).map((r) => r.id));
            next = next.filter((l) => !ids.has(l.rowId));
            partnerBoxes(rows, p.productId, p.need, p.shortfall).forEach((m) => next.push({ key: crypto.randomUUID(), rowId: m.rowId, boxes: m.boxes }));
          }),
      );
      return [...next, newLine()].slice(0, MAX_LINES);
    });
    setInfoOpen(false);
  };

  if (loading) return <LoadingSpinner text="Laai pryse..." />;

  const { totals, currentTier, nextTier } = result;
  const cropId = props.cropId || 1;
  const submitPlan = async () => {
    patchPlan({ busy: true, error: "" });
    try {
      const cropName = (plan.crops.find((c) => c.id === cropId) || {}).name;
      const name = plan.name.trim();
      const out = await createHybridPlan({
        api: HybridPlanAPI,
        cropId,
        planting: { farmerId: plan.farmerId, body: plantingBody(name, hectares, cropName) },
        planName: name,
        items: planPreview.items,
        periods: plan.periods,
      });
      // The same plan object the programs list uses (client, farm, order number ...), so the order screens show their usual header
      let planObj = null;
      try {
        const all = await PlanDetailsAPI.getAllPlans(false, props.agentDetail && props.agentDetail.id, cropId);
        planObj = (all || []).find((p) => p.id === out.plan.id) || null;
      } catch (e) {
        /* fall back below */
      }
      const next = { ...(planObj || out.plan), fromHybrid: true };
      // The documents go in now that the plan has an id. A failed upload never undoes the plan.
      const failedDocs = [];
      for (const doc of docs) {
        try {
          await PlanFilesAPI.upload(out.plan.id, doc);
        } catch (err) {
          failedDocs.push(err.message);
        }
      }
      if (failedDocs.length || !props.setPlanDetails) {
        patchPlan({ busy: false, done: { ...out, next, failedDocs } });
      } else {
        props.setPlanDetails(next);
        props.setPath("orderView");
      }
    } catch (e) {
      patchPlan({
        busy: false,
        error: e.planId
          ? `${e.message}. Program "${e.planName}" (nr ${e.planId}) is gedeeltelik geskep: verwyder dit in die program-lys en probeer weer.`
          : e.message,
      });
    }
  };
  const totalVolume = result.lines.reduce((sum, l) => sum + (l.volume || 0), 0);

  const besparingTip = `Totale lys koste min totale MyPack koste\n= ${money(totals.totalList)} − ${money(totals.totalMyPack)} = ${money(totals.totalDiscount)}${hectares > 0 ? `\nPer ha: ${money(totals.listRpha)} − ${money(totals.myPackRpha)} = ${money(totals.listRpha - totals.myPackRpha)}` : ""}\n\nBron: die Lys koste- en MyPack koste-kolomme in die tabel.`;

  return (
    <div className="total-volume-view" translate="no">
      <Header title="HYBRID VOLUME" backPath="pastOrders" setPath={props.setPath} />
      <main className="total-volume-content hv-page">
        {error && <Message severity="error" text={error} className="tv-message" />}
        {bundlesFailed && (
          <Message
            severity="warn"
            className="tv-message"
            text="Kon nie die weerstandsgegewens laai nie. Die bestelling werk, maar die weerstandstoets word nie gedoen nie."
          />
        )}
        {differing.length > 0 && (
          <Message
            severity="warn"
            className="tv-message"
            text={`Nie al die produkte vir hierdie gewas het dieselfde vlak-minimums nie (bv. ${differing[0].product_name}). Die eerste produk se waardes word gebruik. Jy kan dit hieronder verander.`}
          />
        )}

        <Tooltip
          key={`${infoOpen}-${checks.map((c) => (c.hasMain ? 1 : 0)).join("")}`}
          target=".hv-tip"
          position="top"
          showDelay={150}
          className="hv-tooltip"
        />
        <div className="hv-top">
          <div className="tv-card hv-inputs">
            <div className="hv-bar">INSETTE</div>
            <label htmlFor="hv-ha">Hektaar</label>
            <InputNumber inputId="hv-ha" value={hectares} onValueChange={(e) => setHectares(e.value ?? 0)} min={0} suffix=" ha" />
            <label htmlFor="hv-farm">Plaasnaam</label>
            <InputText id="hv-farm" value={farm} onChange={(e) => setFarm(e.target.value)} />
            <label htmlFor="hv-short" {...tip(TIPS.tekort)}>
              Toelaatbare tekort op vennoot-volumes
            </label>
            <InputNumber
              inputId="hv-short"
              value={shortfallPct ?? dbShortfallPct}
              onValueChange={(e) => setShortfallPct(e.value ?? 0)}
              min={0}
              max={100}
              suffix=" %"
              minFractionDigits={0}
              maxFractionDigits={1}
            />
          </div>

          <div className="hv-info-col">
            {checks.length === 0 && (
              <div className="tv-card hv-info">
                <div className="hv-info-head">
                  <div className="tv-card-title">WEERSTAND VOLUMES</div>
                </div>
                <p className="hv-check-text">Geen weerstandsprodukte vir hierdie gewas nie.</p>
              </div>
            )}
            {checks.map((c) => (
              <div key={c.mainProductId} className={`tv-card hv-info ${c.pass ? "hv-check-ok" : "hv-check-short"}`}>
                <div className="hv-info-head">
                  <Button
                    icon={infoOpen ? "pi pi-chevron-down" : "pi pi-chevron-right"}
                    className="p-button-text p-button-sm hv-toggle"
                    onClick={() => setInfoOpen((o) => !o)}
                    aria-expanded={infoOpen}
                    aria-label={infoOpen ? "Maak toe" : "Maak oop"}
                  />
                  <div className="tv-card-title">{c.mainName.toUpperCase()} weerstand Volumes</div>
                  <span {...tip(TIPS.afslag)} className={`hv-tip hv-badge ${c.pass ? "hv-badge-ok" : "hv-badge-short"}`}>
                    {c.pass ? `${c.mainName} afslag: Ja` : `${c.mainName} afslag: Nee`}
                  </span>
                </div>
                {!infoOpen && !c.pass && c.hasMain && <p className="hv-check-text">{c.message}</p>}
                {infoOpen && (
                  <>
                    <p className="hv-check-text">
                      {c.hasMain
                        ? `${qty(c.mainLitres)} L ${c.mainName} dek ${qty(c.hectaresCovered)} ha: ${dose(c.mainDose)} per spuit × ${c.mainSprays} spuite per seisoen = ${dose(c.mainDose * c.mainSprays)} per ha. Elke vennoot moet dieselfde hektaar dek (minus ${dose(c.partners.length ? c.partners[0].shortfall * 100 : dbShortfallPct)}% tekort).`
                        : `Geen ${c.mainName} in die bestelling nie. Kies dit in die tabel en tik die aantal bokse om die vereiste vennote-volumes te sien.`}
                    </p>
                    <table className="tv-table hv-partners">
                      <thead>
                        <tr>
                          <th {...tip(TIPS.vennoot)}>Vennoot</th>
                          <th {...tip(TIPS.dosis, "num")}>Dosis per spuit</th>
                          <th {...tip(TIPS.spuite, "num")}>Spuite per seisoen</th>
                          <th {...tip(TIPS.minNodig, "num")}>Minimum nodig (L/kg)</th>
                          <th {...tip(TIPS.huidig, "num")}>Huidig bestel (L/kg)</th>
                          <th {...tip(TIPS.vennootToets)}>Toets</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.partners.map((p) => (
                          <tr key={p.key}>
                            <td>
                              {p.options.length > 1 ? (
                                <Dropdown
                                  value={p.productId}
                                  options={p.options.map((o) => ({ label: o.name, value: o.productId }))}
                                  onChange={(e) => setChoices((ch) => ({ ...ch, [p.key]: e.value }))}
                                />
                              ) : (
                                p.name
                              )}
                            </td>
                            <td className="num">{dose(p.dose)}</td>
                            <td className="num">{p.sprays}</td>
                            <td
                              {...tip(
                                `${qty(c.hectaresCovered)} ha × ${dose(p.dose)} × ${p.sprays} spuite = ${dose(p.need)}; minus ${dose(p.shortfall * 100)}% tekort, na bo afgerond = ${qty(p.needed)}`,
                                "num",
                              )}
                            >
                              {qty(p.needed)}
                            </td>
                            <td className={`num ${c.hasMain ? (p.ok ? "hv-good-cell" : "hv-bad-cell") : ""}`}>{qty(p.ordered)}</td>
                            <td>{c.hasMain && <span className={`hv-chk ${p.ok ? "hv-chk-ok" : "hv-chk-bad"}`}>{p.ok ? "Reg" : "Te min"}</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {c.hasMain && !c.pass && (
                      <Message
                        severity="warn"
                        className="tv-message"
                        text={`${c.message}. ${c.mainName} kry nie die weerstandsprys nie; die prys volg sy eie vlak-instellings. Die bestelling kan steeds ingedien word; as die agent tog kwalifiseer word die prysvorm met die hand gemaak.`}
                      />
                    )}
                    <span
                      className="hv-add-wrap"
                      title={
                        !c.hasMain
                          ? `Kies eers ${c.mainName} en tik die aantal bokse`
                          : c.pass
                            ? "Al die vennote is genoeg bestel"
                            : "Voeg die minimum bokse van die vennote by die bestelling"
                      }
                    >
                      <Button
                        label="Voeg produkte by"
                        icon="pi pi-plus-circle"
                        className="hv-add"
                        disabled={!c.hasMain || c.pass}
                        onClick={addMinimumPartners}
                      />
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="tv-card hv-tiers">
            <div className="hv-bar">MYPACK VLAKKE</div>
            <div className="tv-threshold">
              <span {...tip(TIPS.tier1)}>Vlak 1 lys R/ha</span>
              <InputNumber value={thresholds.tier1MinRpha} onValueChange={setThreshold("tier1MinRpha")} min={0} />
            </div>
            <div className="tv-threshold">
              <span {...tip(TIPS.tier2)}>Vlak 2 lys R/ha</span>
              <InputNumber value={thresholds.tier2MinRpha} onValueChange={setThreshold("tier2MinRpha")} min={0} />
            </div>
            <div className="tv-threshold">
              <span {...tip(TIPS.tier3)}>Vlak 3 totale lys faktuur</span>
              <InputNumber value={thresholds.tier3MinInvoice} onValueChange={setThreshold("tier3MinInvoice")} min={0} />
            </div>
            <div className="hv-kv">
              <span
                {...tip(
                  `Totale lys faktuur ÷ hektaar${hectares > 0 ? `\n= ${money(totals.totalList)} ÷ ${dose(hectares)} ha = ${money(totals.listRpha)}` : ""}\n\nBron: die Lys koste-kolom en die Hektaar-veld onder INSETTE. Dit bepaal Vlak 1 en Vlak 2.`,
                )}
              >
                Lys R/ha
              </span>
              <strong>{money(totals.listRpha)}</strong>
            </div>
            <div className="hv-kv">
              <span {...tip(TIPS.kvFaktuur)}>Totale lys faktuur</span>
              <strong>{money(totals.totalList)}</strong>
            </div>
            <div className="hv-kv">
              <span {...tip(TIPS.kvVlak)}>Huidige vlak</span>
              <strong className={`hv-tier ${currentTier > 0 ? "hv-good-cell" : "hv-bad-cell"}`}>{TIER_LABEL[currentTier]}</strong>
            </div>
            <div className="hv-kv">
              <span {...tip(TIPS.kvVolgende)}>Volgende vlak</span>
              <strong>{nextTier ? `${money(nextTier.moreNeeded)} meer vir Vlak ${nextTier.tier}` : "Hoogste vlak bereik"}</strong>
            </div>
          </div>
        </div>

        <div className="hv-bar hv-bar-grid">VOLUME BESTELLING – kies &#39;n produk en tik die aantal bokse</div>
        <div className="tv-table-wrap">
          <table className="tv-table">
            <thead>
              <tr>
                <th {...tip(TIPS.produk)}>Produk</th>
                <th {...tip(TIPS.kode, "num")}>Kode</th>
                <th {...tip(TIPS.pak, "num")}>Pakgrootte (L/kg per boks)</th>
                <th {...tip(TIPS.bokse)}>Bokse</th>
                <th {...tip(TIPS.volume, "num")}>Volume (L/kg)</th>
                <th {...tip(TIPS.volHa, "num")}>Vol/ha</th>
                <th {...tip(TIPS.min, "num")}>Min vol/ha</th>
                <th {...tip(TIPS.maks, "num")}>Maks vol/ha</th>
                <th {...tip(TIPS.toets)}>Toets</th>
                <th {...tip(TIPS.lysPrys, "num")}>Lys prys (R/L)</th>
                <th {...tip(TIPS.mypackPrys, "num")}>MyPack prys (R/L)</th>
                <th {...tip(TIPS.lysKoste, "num")}>Lys koste (R)</th>
                <th {...tip(TIPS.mypackKoste, "num")}>MyPack koste (R)</th>
                <th {...tip(TIPS.lysHa, "num")}>Lys R/ha</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const r = resultByKey.get(line.key);
                const row = rows.find((x) => x.id === line.rowId);
                const grp = groupAfterKey.get(line.key);
                return (
                  <Fragment key={line.key}>
                    <tr>
                      <td className={`tv-product ${r && r.checks.includes("Duplicate") ? "hv-bad-cell" : ""}`}>
                        <Dropdown
                          value={line.rowId}
                          options={productOptions(line)}
                          onChange={(e) => updateLine(line.key, { rowId: e.value })}
                          placeholder="Kies produk"
                          filter
                          showClear
                        />
                      </td>
                      <td className="num">{row ? row.AGI : ""}</td>
                      <td className="num">{row ? dose(num(row.full_pack_volume)) : ""}</td>
                      <td>
                        <InputNumber value={line.boxes} onValueChange={(e) => updateLine(line.key, { boxes: e.value })} min={0} disabled={!line.rowId} />
                      </td>
                      <td className="num">{r && r.volume ? dose(r.volume) : ""}</td>
                      <td className={`num ${volClass(r)}`}>{r && r.volume ? dose(r.volPerHa) : ""}</td>
                      <td className="num">{r ? dose(r.minDosage) : ""}</td>
                      <td className="num">{r ? dose(r.maxDosage) : ""}</td>
                      <td>{r ? checkCell(r.checks, r.minDosage, r.maxDosage, !!r.volume) : null}</td>
                      <td className="num hv-green">{row ? price(row.list_price_pv) : ""}</td>
                      <td className="num hv-green">
                        {r && r.volume ? price(r.unitPrice) : ""}
                        <span className="hv-star" title={r && r.seasonCapped ? "Seisoenprys" : undefined}>
                          {r && r.volume && r.seasonCapped ? "*" : ""}
                        </span>
                        {r && r.volume && r.bundleMain && (r.resistancePriced || Math.abs(r.listValue - r.myPackValue) < 0.5) ? (
                          <div className="hv-tag-line">
                            <span className={`hv-tag ${r.resistancePriced ? "hv-tag-ok" : "hv-tag-list"}`}>
                              {r.resistancePriced ? "weerstandsprys" : "lysprys"}
                            </span>
                          </div>
                        ) : null}
                      </td>
                      <td className="num">{r && r.volume ? money(r.listValue) : ""}</td>
                      <td className="num">{r && r.volume ? money(r.myPackValue) : ""}</td>
                      <td className="num">{r && r.volume && hectares > 0 ? money(r.listValue / hectares) : ""}</td>
                      <td>
                        <Button icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => removeLine(line.key)} aria-label="Verwyder" />
                      </td>
                    </tr>
                    {grp && (
                      <tr className="tv-subtotal">
                        <td colSpan={3}>Totaal {grp.name}</td>
                        <td className="hv-boxes-total">{grp.boxes || ""}</td>
                        <td className="num">{dose(grp.volume)}</td>
                        <td className="num">{dose(grp.volPerHa)}</td>
                        <td className="num">{dose(grp.minDosage)}</td>
                        <td className="num">{dose(grp.maxDosage)}</td>
                        <td>{checkCell(grp.checks, grp.minDosage, grp.maxDosage, true)}</td>
                        <td />
                        <td />
                        <td className="num">{money(grp.listValue)}</td>
                        <td className="num">{money(grp.myPackValue)}</td>
                        <td className="num">{hectares > 0 ? money(grp.listValue / hectares) : ""}</td>
                        <td />
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td>Totaal</td>
                <td colSpan={2} />
                <td className="hv-boxes-total">{totals.totalBoxes || ""}</td>
                <td className="num">{totalVolume ? dose(totalVolume) : ""}</td>
                <td colSpan={6} />
                <td className="num">{money(totals.totalList)}</td>
                <td className="num">{money(totals.totalMyPack)}</td>
                <td />
                <td />
              </tr>
              <tr>
                <td
                  colSpan={11}
                  {...tip(
                    `Lys en MyPack koste ÷ hektaar${hectares > 0 ? `\nLys: ${money(totals.totalList)} ÷ ${dose(hectares)} ha = ${money(totals.listRpha)}\nMyPack: ${money(totals.totalMyPack)} ÷ ${dose(hectares)} ha = ${money(totals.myPackRpha)}` : ""}`,
                  )}
                >
                  Koste per ha
                </td>
                <td className="num">{money(totals.listRpha)}</td>
                <td className="num">{money(totals.myPackRpha)}</td>
                <td />
                <td />
              </tr>
              <tr>
                <td colSpan={11} {...tip(besparingTip)}>
                  MyPack besparing teenoor lys
                </td>
                <td colSpan={2} {...tip(besparingTip, "num")}>
                  {money(totals.totalDiscount)}
                  <div className="hv-sub">({money(totals.listRpha - totals.myPackRpha)}/ha)</div>
                </td>
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="tv-actions">
          <Button
            label="Voeg produk by"
            icon="pi pi-plus"
            className="p-button-outlined"
            disabled={lines.length >= MAX_LINES}
            onClick={() => setLines((ls) => [...ls, newLine()])}
          />
          <Button label="Stoor dokument" icon="pi pi-upload" className="p-button-outlined" onClick={() => docInput.current && docInput.current.click()} />
          <input ref={docInput} type="file" accept={ACCEPT} multiple style={{ display: "none" }} onChange={pickDocs} />
          <Button label="Heg spuit program aan" icon="pi pi-paperclip" disabled={plan.open} onClick={openPlan} />
        </div>
        {plan.open && (
          <div className="hv-plan">
            <div className="hv-bar">HEG SPUIT PROGRAM AAN</div>
            <div className="hv-plan-body">
              {plan.done ? (
                <>
                  <Message severity="success" text={`Program "${plan.done.plan.planName}" is geskep met ${planPreview.items.length} produk(te).`} />
                  {plan.done.failedDocs && plan.done.failedDocs.length > 0 && (
                    <Message severity="warn" text={`${plan.done.failedDocs.join(". ")}. Jy kan die dokument later op die bestelskerm byvoeg.`} />
                  )}
                  {plan.done.notes.map((n) => (
                    <Message key={n} severity="warn" text={n} />
                  ))}
                  <div className="tv-actions">
                    <Button
                      label="Gaan na bestelling"
                      icon="pi pi-arrow-right"
                      onClick={() => {
                        if (props.setPlanDetails) {
                          props.setPlanDetails(plan.done.next);
                          props.setPath("orderView");
                        } else {
                          props.setPath("pastOrders");
                        }
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="hv-plan-fields">
                    <label>
                      Kies &#39;n kliënt
                      <Dropdown
                        value={plan.farmerId}
                        options={plan.farmers.map((f) => ({ label: f.businessName || f.farmName, value: f.id }))}
                        onChange={(e) => patchPlan({ farmerId: e.value })}
                        placeholder="Kies kliënt"
                        filter
                      />
                    </label>
                    <label>
                      Program naam
                      <InputText value={plan.name} onChange={(e) => patchPlan({ name: e.target.value })} />
                    </label>
                    <label>
                      Aanplanting grootte (Ha)
                      <InputText value={String(hectares)} disabled />
                    </label>
                  </div>
                  <table className="hv-plan-table">
                    <thead>
                      <tr>
                        <th>Produk</th>
                        <th className="num">Volume</th>
                        <th className="num">Dosis per spuit</th>
                        <th className="num">Weke</th>
                        <th>Bokse</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planPreview.items.map((it) => (
                        <tr key={it.productId}>
                          <td>{it.name}</td>
                          <td className="num">{dose(it.volume)}</td>
                          <td className="num">{dose(it.dosage)}</td>
                          <td className="num">{it.weeks}</td>
                          <td>
                            {Object.keys(it.packs)
                              .map((k) => `${it.packs[k]} × ${k}`)
                              .join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {docs.length > 0 && <div className="hv-sub">Dokumente wat saam gestoor word: {docs.map((d) => d.name).join(", ")}</div>}
                  {planPreview.skipped.map((x) => (
                    <Message key={x.name + x.reason} severity="warn" text={`${x.name} word nie ingesluit nie: ${x.reason}.`} />
                  ))}
                  {plan.error && <Message severity="error" text={plan.error} />}
                  <div className="tv-actions">
                    <Button
                      label="Skep program en gaan na bestelling"
                      icon="pi pi-check"
                      disabled={plan.busy || !plan.farmerId || !plan.name.trim() || !planPreview.items.length}
                      onClick={submitPlan}
                    />
                    <Button label="Kanseleer" className="p-button-outlined" disabled={plan.busy} onClick={() => patchPlan({ open: false })} />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {(docs.length > 0 || docError) && (
          <div className="hv-docs">
            {docs.map((d) => (
              <span key={d.name + d.size} className="hv-doc">
                <i className="pi pi-file" /> {d.name} <span className="hv-sub">{formatSize(d.size)}</span>
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-sm"
                  aria-label="Verwyder"
                  onClick={() => setDocs((l) => l.filter((x) => x !== d))}
                />
              </span>
            ))}
            {docs.length > 0 && <span className="hv-sub">Word saam met die program gestoor (enige lêer, tot {MAX_FILE_MB} MB).</span>}
            {docError && <Message severity="error" text={docError} />}
          </div>
        )}
        {notice && <Message severity="info" text={notice} className="tv-message" />}

        <p className="tv-note">
          Beraming. Die finale prys word op die bestelling bepaal (weer/registrasie-toetse is nie hier ingereken nie). * = seisoenprys toegepas. &#39;n
          Weerstandsproduk (bv. SILVALIS) kry die weerstandsprys wanneer al sy vennote genoeg bestel is; anders volg sy prys sy eie vlak-instellings in die
          databasis.
        </p>
      </main>
    </div>
  );
};

export default HybridVolume;
